import { Link } from "react-router-dom";
import { Award, Calculator, GraduationCap, Handshake, MapPin, ShieldCheck, Users } from "lucide-react";
import SEO from "@/components/SEO";
import { Button } from "@/components/ui/button";

export default function About() {
  return (
    <div className="bg-navy-base min-h-screen" data-testid="about-page">
      <SEO title="About XTM TECHNOLOGIES — The FutureSkills Academy, Jorhat"
        description="XTM TECHNOLOGIES — The FutureSkills Academy in Jorhat, Assam. Enlightening Emerging Technologies. ISO 9001:2015 Certified, MSME and GST registered." />
      <section className="relative hero-glow">
        <div className="absolute inset-0 cyber-grid opacity-40" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="text-xs uppercase tracking-[0.2em] font-semibold text-orange-400 mb-3">About XTM</div>
          <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-white mb-6" data-testid="about-heading">
            Enlightening <span className="text-gradient-orange">Emerging Technologies</span>
          </h1>
          <p className="text-lg text-slate-300 max-w-3xl leading-relaxed">
            XTM TECHNOLOGIES — The FutureSkills Academy is a technology-driven skills academy in Jorhat, Assam.
            We help learners Discover → Learn → Practice → Certify → Intern → Build Careers → Keep Growing,
            starting from our Jorhat centre and progressively serving learners across Assam and Northeast India.
          </p>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 grid md:grid-cols-3 gap-6" data-testid="about-values">
        {[
          [GraduationCap, "Our Mission", "Make future-ready skills — AI, data, software, electronics, business and professional skills — accessible and practical for learners in Assam."],
          [Users, "How We Teach", "Theory + Practical + Project in every program, small batches, mentor support, and verifiable certification."],
          [Award, "Career Ecosystem", "Career pathways, internship opportunities, workshops, counselling and an AI-powered course finder — all connected in one platform."],
        ].map(([Icon, t, d]) => (
          <div key={t} className="bg-card border border-white/10 rounded-2xl p-7">
            <Icon className="w-7 h-7 text-orange-400 mb-4" />
            <h3 className="font-display text-lg font-semibold text-white mb-2">{t}</h3>
            <p className="text-sm text-slate-400 leading-relaxed">{d}</p>
          </div>
        ))}
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-16" data-testid="about-partnerships">
        <div className="text-xs uppercase tracking-[0.2em] font-semibold text-orange-400 mb-3">Partnerships & Accreditations</div>
        <h2 className="font-display text-2xl sm:text-3xl font-bold text-white mb-8">Backed by Recognized Institutions</h2>
        <div className="grid md:grid-cols-3 gap-6">
          <div className="bg-card border border-orange-500/30 rounded-2xl p-7" data-testid="partner-aisect">
            <Handshake className="w-7 h-7 text-orange-400 mb-4" />
            <h3 className="font-display text-lg font-semibold text-white mb-1">AISECT India</h3>
            <p className="text-[11px] uppercase tracking-wider text-orange-400 font-semibold mb-3">Knowledge Skill Provider</p>
            <p className="text-sm text-slate-400 leading-relaxed">XTM TECHNOLOGIES is the Knowledge Skill Provider of AISECT India, delivering structured skill development programs.</p>
          </div>
          <div className="bg-card border border-orange-500/30 rounded-2xl p-7" data-testid="partner-sgsu">
            <GraduationCap className="w-7 h-7 text-orange-400 mb-4" />
            <h3 className="font-display text-lg font-semibold text-white mb-1">Scope Global Skill University (SGSU), Bhopal</h3>
            <p className="text-[11px] uppercase tracking-wider text-orange-400 font-semibold mb-3">Industry Collaborator — WILP Vocational Programs</p>
            <p className="text-sm text-slate-400 leading-relaxed">XTM TECHNOLOGIES is an Industry Collaborator with SGSU Bhopal for implementing the Work-Integrated Learning Programme (WILP) for <strong className="text-slate-200">B.Voc. in Application Development</strong> and <strong className="text-slate-200">B.Voc. in Information Technology</strong>.</p>
          </div>
          <div className="bg-card border border-orange-500/30 rounded-2xl p-7" data-testid="partner-tally">
            <Calculator className="w-7 h-7 text-orange-400 mb-4" />
            <h3 className="font-display text-lg font-semibold text-white mb-1">Tally Education</h3>
            <p className="text-[11px] uppercase tracking-wider text-orange-400 font-semibold mb-3">Authorised Training Center</p>
            <p className="text-sm text-slate-400 leading-relaxed">XTM TECHNOLOGIES is an Authorised Training Center of Tally Education, delivering TallyPrime and commerce-skills training.</p>
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-20">
        <div className="bg-card border border-white/10 rounded-2xl p-8 grid md:grid-cols-2 gap-8 items-center" data-testid="about-credentials">
          <div>
            <h2 className="font-display text-2xl font-bold text-white mb-4">Organization Credentials</h2>
            <div className="flex flex-wrap gap-2.5 mb-6">
              {["ISO 9001:2015 Certified", "MSME Registered", "GST Registered"].map((c) => (
                <span key={c} className="flex items-center gap-2 text-xs font-semibold text-slate-200 border border-orange-500/40 rounded-full px-3.5 py-2">
                  <ShieldCheck className="w-3.5 h-3.5 text-orange-400" />{c}
                </span>
              ))}
            </div>
            <p className="text-sm text-slate-400 flex gap-2"><MapPin className="w-4 h-4 text-orange-400 shrink-0 mt-0.5" /> Katyayani Building, 3rd Floor, Near Star Media, Tarajan, Jorhat – 785001, Assam</p>
          </div>
          <div className="flex flex-col sm:flex-row gap-3 md:justify-end">
            <Button asChild className="bg-orange-600 hover:bg-orange-700 text-white font-semibold rounded-full px-7 transition-colors" data-testid="about-contact-btn">
              <Link to="/contact">Contact Us</Link>
            </Button>
            <Button asChild variant="outline" className="border-white/20 text-white hover:bg-white/5 rounded-full px-7 transition-colors" data-testid="about-courses-btn">
              <Link to="/courses">Explore Courses</Link>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
}
